import Component from '@glimmer/component'
import { action } from '@ember/object'
import { tracked } from '@glimmer/tracking'
import { isPresent } from '@ember/utils'
import { service } from '@ember/service'
import { timeout, task } from 'ember-concurrency'
import {
  tableColumnsToComparerKeyMapping,
  injectComparerKeys,
  type SortingConfig,
  type SortComparerKey,
} from '@blakeelearning/data-tables/utils/sorting'
import type { IntlService } from 'ember-intl'
import type DataTransformerService from '@blakeelearning/data-tables/services/data-transformer'
import type { PaginationConfig } from '@blakeelearning/data-tables/utils/paging'
import type { PipelineDescriptor } from '@blakeelearning/data-tables/utils/data-transformer'
import type { SafeString } from '@ember/template'
import type { FormatValueOptions } from 'teacher-ui-client/utils/format-value'
import applyTransform from '@blakeelearning/data-tables/modifiers/apply-transform'
import BasicTable from 'teacher-ui-client/components/stable-table/basic-table'
import SortableCell from 'teacher-ui-client/components/stable-table/basic-cells/sortable'
import ClickableCell from 'teacher-ui-client/components/stable-table/basic-cells/clickable'
import { hash } from '@ember/helper'
import { and, not } from 'ember-truth-helpers'
import { FidgetSpinnerWaveComponent } from '@blakeelearning/fidget/components/fidget/spinner/wave'
import type { RowObject } from 'teacher-ui-client/components/stable-table/basic-table/cell'
import { BasePageSelector } from 'teacher-ui-client/components/base/page-selector'

interface Args {
  sortingConfig?: SortingConfig
  paginationConfig?: PaginationConfig
  students?: StudentItem[]
  onStudentClick?: (student_id: string) => void
  exportable?: boolean
  onSortClick?: (sortObject: SortObject) => unknown
}
interface Signature {
  Args: Args
}

interface StudentItem extends RowObject {
  student_id: number
}
interface Column {
  value?: string | SafeString
  cellClassNames?: string
  valuePath?: string
  sortKey?: string
  comparerKey?: SortComparerKey
  classNames?: string
  component?: typeof SortableCell
  formatEmptyConfig?: FormatValueOptions
}

export class TablesPeaksProgressStudentsComponent extends Component<Signature> {
  @service intl!: IntlService

  @service dataTransformer!: DataTransformerService

  @tracked pageCount = 1

  @tracked transformedItems: StudentItem[] = []

  @tracked unpaginatedItems: StudentItem[] = []

  /**
   * Returns an array of operation descriptions that will be applied to the items
   */
  get pipelineDescriptors() {
    return [this.sortingDescriptor, this.paginationDescriptor]
  }

  get sortingDescriptor() {
    const tableColumns = this.columns
    if (!this.args.sortingConfig) return null

    const comparerKeyMapping = tableColumnsToComparerKeyMapping(tableColumns)
    const sortingConfigWithComparerKeys = injectComparerKeys(this.args.sortingConfig, comparerKeyMapping)
    const sortDescriptor = { type: 'sort', config: sortingConfigWithComparerKeys }

    return sortDescriptor
  }

  get paginationDescriptor() {
    if (!this.args.paginationConfig) return null
    return { type: 'paginate', config: this.args.paginationConfig }
  }

  /**
   * This task provides an array of items and operations to the data transformer service, which will asynchronously
   * transform the data according to the operations, then set those transformed items for display in the table
   */
  @action
  performTransformItems(_element: unknown, [items, pipelineDescriptors]: [Args['students'], PipelineDescriptor[]]) {
    void this.transformItems.perform(items, pipelineDescriptors)
  }

  transformItems = task(
    { restartable: true },
    async (items: Args['students'] = [], pipelineDescriptors: PipelineDescriptor[] = []) => {
      await timeout(200) // debounce

      const presentDescriptors = pipelineDescriptors.filter(isPresent)
      const transformedData = await this.dataTransformer.buildAndTransform<StudentItem, StudentItem>(
        items,
        presentDescriptors,
      )

      this.pageCount = transformedData.pageCount
      this.transformedItems = transformedData.items
      this.unpaginatedItems = transformedData.paginated?.flat() ?? []
    },
  )

  get defaultColumn(): Column {
    return {
      classNames: 'table-box table-box-header bg-oceany-blue-100',
      cellClassNames: 'table-box table-box-cell bg-oceany-blue-50 group-hover:bg-oceany-blue-100',
      component: SortableCell,
      comparerKey: 'sortByNumber',
    }
  }

  get columns(): Column[] {
    const { intl, defaultColumn } = this
    const translationPrefix = 'tables.peaksProgress.students.headers'

    const columns: Column[] = [
      {
        ...defaultColumn,
        value: intl.t(`${translationPrefix}.firstName`),
        valuePath: 'firstName',
        comparerKey: 'sortByValue',
      },
      {
        ...defaultColumn,
        value: intl.t(`${translationPrefix}.lastName`),
        valuePath: 'lastName',
        comparerKey: 'sortByValue',
      },
      {
        ...defaultColumn,
        value: intl.t(`${translationPrefix}.className`),
        valuePath: 'schoolClassNames',
        comparerKey: 'sortByValue',
      },
      {
        ...defaultColumn,
        value: intl.t(`${translationPrefix}.peaksCompleted`),
        valuePath: 'lessons_completed',
      },
      {
        ...defaultColumn,
        value: intl.t(`${translationPrefix}.soundGemsEarned`),
        valuePath: 'sound_gems_earned',
      },
      {
        ...defaultColumn,
        value: intl.t(`${translationPrefix}.wordGemsEarned`),
        valuePath: 'word_gems_earned',
      },
      {
        ...defaultColumn,
        value: intl.t(`${translationPrefix}.booksRead`),
        valuePath: 'books_read',
      },
      {
        ...defaultColumn,
        value: intl.t(`${translationPrefix}.averageQuizScore`),
        valuePath: 'average_score',
        formatEmptyConfig: { append: '%' },
      },
    ]

    return columns
  }

  get exportColumns() {
    const { intl, defaultColumn, columns } = this
    const [firstName, lastName, ...rest] = columns

    return [
      firstName,
      lastName,
      {
        ...defaultColumn,
        value: intl.t('reporting.dashboardTables.studentExternalId'),
        valuePath: 'externalId',
      },
      ...rest,
    ]
  }

  get studentRows() {
    const {
      transformedItems,
      args: { onStudentClick },
    } = this

    return transformedItems.map((student) => {
      const nameCell = {
        component: ClickableCell,
        componentClassNames: 'cursor-pointer text-oceany-blue-325 font-bold print:wrap-break-word',
        onClick() {
          onStudentClick?.(String(student.student_id))
        },
      }

      return {
        ...student,
        classNames: 'table-row-striped group',
        cells: [{ ...nameCell }, { ...nameCell }, {}, {}, {}, {}, {}, {}],
      }
    })
  }

  get studentRowsForExport() {
    return this.unpaginatedItems
  }

  get noDataMessage() {
    return this.intl.t('tables.peaksProgress.emptyTableMessage')
  }

  <template>
    <div {{applyTransform this.performTransformItems @students this.pipelineDescriptors}}>
      <BasicTable
        class="relative w-full"
        @columns={{this.columns}}
        @rows={{this.studentRows}}
        @config={{hash sortingConfig=@sortingConfig onSortClick=@onSortClick}}
        as |bt|
      >
        <bt.head />

        {{! If not transforming and no students to show, always only display the empty message }}
        {{#if (and this.transformItems.isIdle (not this.studentRows))}}
          <bt.spanner class="p-3 text-center italic">
            {{this.noDataMessage}}
          </bt.spanner>
        {{else}}
          {{! If transforming, always show a loading spinner}}
          {{! When no items, show it normally. }}
          {{! If there are items, place it on top of the existing table body (absolute) }}
          {{#if this.transformItems.isRunning}}
            <bt.spanner
              class="table-box table-box-cell border-transparent
                {{if this.studentRows 'absolute left-1/2 -translate-x-1/2 transform'}}"
            >
              <FidgetSpinnerWaveComponent @small={{true}} @centered={{true}} />
            </bt.spanner>
          {{/if}}
          <bt.body class={{if this.transformItems.isRunning "opacity-50"}} />
        {{/if}}

      </BasicTable>
      {{#if @exportable}}
        <BasicTable
          class="hidden"
          exportable={{true}}
          @columns={{this.exportColumns}}
          @rows={{this.studentRowsForExport}}
          @config={{hash sortingConfig=@sortingConfig}}
          as |bt|
        >
          <bt.head />
          {{#if this.studentRowsForExport}}
            <bt.body />
          {{/if}}
        </BasicTable>
      {{/if}}

      <BasePageSelector class="mx-2 my-4" @pageCount={{this.pageCount}} @itemCount={{this.unpaginatedItems.length}} />
    </div>
  </template>
}

export default TablesPeaksProgressStudentsComponent
